// Bundle splitting and optimization utilities

// Preload critical routes
export const preloadRoute = (routePath: string) => {
  if (typeof window === 'undefined') return;

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = routePath;
  document.head.appendChild(link);
};

// Preload critical components
export const preloadComponent = (importFn: () => Promise<any>) => {
  if (typeof window === 'undefined') return;

  // Only preload if the user is on a fast connection
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection && (connection.effectiveType === '4g' || connection.effectiveType === '5g')) {
      // Preload with a small delay to not block initial rendering
      setTimeout(() => {
        importFn().catch(() => {
          // Silently fail if preload doesn't work
        });
      }, 1000);
    }
  }
};

// Dynamic import with retry logic
export const importWithRetry = async (
  importFn: () => Promise<any>,
  retries = 3,
  delay = 1000
): Promise<any> => {
  try {
    return await importFn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return importWithRetry(importFn, retries - 1, delay * 2);
    }
    throw error;
  }
};

// Preload critical chunks based on user behavior
export const initializePreloading = () => {
  if (typeof window === 'undefined') return;

  // Preload shop picks when user is on homepage
  if (window.location.pathname === '/') {
    preloadComponent(() => import('../pages/ShopPicksOptimized'));
    preloadComponent(() => import('../pages/PurchaseOptions'));
  }

  // Preload dashboard when user is authenticated
  const user = localStorage.getItem('user');
  if (user) {
    preloadComponent(() => import('../pages/Dashboard'));
    preloadComponent(() => import('../pages/MyPicks'));
  }

  // Preload based on mouse hover events
  document.addEventListener('mouseover', (event) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href]') as HTMLAnchorElement;

    if (link && link.href) {
      const path = new URL(link.href).pathname;

      // Preload specific routes on hover
      if (path.includes('/shop-picks') && !path.includes('/purchase/')) {
        preloadComponent(() => import('../pages/ShopPicksOptimized'));
      } else if (path.includes('/purchase/')) {
        preloadComponent(() => import('../pages/PurchaseOptions'));
      } else if (path.includes('/dashboard')) {
        preloadComponent(() => import('../pages/Dashboard'));
      }
    }
  });
};

// Resource hints for better loading
export const addResourceHints = () => {
  if (typeof document === 'undefined') return;

  // Preconnect to API
  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  document.head.appendChild(preconnect);

  // DNS prefetch for external resources
  const dnsPrefetch = document.createElement('link');
  dnsPrefetch.rel = 'dns-prefetch';
  dnsPrefetch.href = 'https://fonts.googleapis.com';
  document.head.appendChild(dnsPrefetch);
};

// Intersection Observer for lazy loading
export const createLazyLoader = (threshold = 0.1) => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }

  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          const componentName = target.dataset.lazyComponent;

          if (componentName) {
            // Trigger component loading
            const event = new CustomEvent('lazyload-component', {
              detail: { componentName }
            });
            target.dispatchEvent(event);
          }
        }
      });
    },
    { threshold }
  );
};